import React, { useEffect } from 'react';
import useHorizontalScroll from '../../Hooks/useHorizontalScroll';

/**
 * A wrapper component that provides horizontal scrolling and navigation buttons.
 * It automatically scrolls to center an active element (aria-current="page" or aria-selected="true")
 * on initial load if initialScrollEnabled is true.
 */
function ScrollableContainer({
    scrollStep = 200,
    initialScrollEnabled = false,
    children,
    ...props
}) {
    const {
        containerRef,
        prevButtonRef,
        nextButtonRef,
        isPrevDisabled,
        isNextDisabled
    } = useHorizontalScroll(scrollStep);

    useEffect(() => {
        if (!initialScrollEnabled || !containerRef.current) return;

        const container = containerRef.current;
        
        let targetElement = container.querySelector('[aria-current="page"]') ||
                            container.querySelector('[aria-selected="true"]');

        if (targetElement) {
            const containerRect = container.getBoundingClientRect();
            const targetRect = targetElement.getBoundingClientRect();

            if (targetElement === container.firstElementChild) {
                if (container.scrollLeft > 1) {
                    container.scrollTo({ left: 0, behavior: 'smooth' });
                }
            } 
            else if (targetElement === container.lastElementChild) {
                container.scrollTo({
                    left: container.scrollWidth - container.clientWidth + 2,
                    behavior: 'smooth'
                });
            } 
            else {
                const offset = targetRect.left - containerRect.left - containerRect.width / 2 + targetRect.width / 2;
                
                if (Math.abs(offset) > 1) {
                    container.scrollBy({
                        left: offset,
                        behavior: 'smooth'
                    });
                }
            }
        }
    }, [initialScrollEnabled, containerRef]);

    return (
        <div data-scroll-wrapper {...props}>
            <button
                data-scroll-prev-button
                onClick={prevButtonRef.current}
                disabled={isPrevDisabled}
                className='px-2 py-[20px] rounded-full bg-grey-lighter shadow-md flex items-center justify-center text-text-secondary hover:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none transition-all'
                style={{ visibility: isPrevDisabled && isNextDisabled ? 'hidden' : 'visible' }} 
            >
              <span className="icon">chevron_left</span>
            </button>

            <div
                ref={containerRef}
                data-scroll-container
                className='relative flex text-center bg-grey-lighter rounded-full overflow-x-auto scrollbar-hidden'
            >
                {children}
            </div>

            <button
                data-scroll-next-button
                onClick={nextButtonRef.current}
                disabled={isNextDisabled}
                className='px-2 py-[20px] rounded-full bg-grey-lighter shadow-md flex items-center justify-center text-text-secondary hover:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none transition-all'
                style={{ visibility: isPrevDisabled && isNextDisabled ? 'hidden' : 'visible' }}
            >
                <span className="icon">chevron_right</span>
            </button>
        </div>
    );
}

export default ScrollableContainer;